/**
 * Context: ProjectContext — Project-First Navigation
 * Persists active project to localStorage; provides viewAll toggle for global nav
 */
import { createContext, useContext, useState, ReactNode } from 'react'

export interface ActiveProject {
  id: string | number
  name: string
  status?: string
}

interface ProjectContextType {
  activeProject: ActiveProject | null
  setActiveProject: (p: ActiveProject | null) => void
  viewAll: boolean
  setViewAll: (v: boolean) => void
}

const ProjectContext = createContext<ProjectContextType>({
  activeProject: null,
  setActiveProject: () => {},
  viewAll: false,
  setViewAll: () => {},
})

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [activeProject, setActiveProjectState] = useState<ActiveProject | null>(() => {
    try {
      return JSON.parse(localStorage.getItem('ml_active_project') || 'null')
    } catch {
      return null
    }
  })
  const [viewAll, setViewAllState] = useState(() => localStorage.getItem('ml_view_all') === 'true')

  function setActiveProject(p: ActiveProject | null) {
    setActiveProjectState(p)
    if (p) localStorage.setItem('ml_active_project', JSON.stringify(p))
    else localStorage.removeItem('ml_active_project')
  }

  function setViewAll(v: boolean) {
    setViewAllState(v)
    if (v) localStorage.setItem('ml_view_all', 'true')
    else localStorage.removeItem('ml_view_all')
  }

  return (
    <ProjectContext.Provider value={{ activeProject, setActiveProject, viewAll, setViewAll }}>
      {children}
    </ProjectContext.Provider>
  )
}

export const useProject = () => useContext(ProjectContext)
